import { App_Url_New } from "@/shared/common/static";
import { getHomeCountryLabel, LOCALISATION_CONFIG } from "@/shared/config/localisation";
import { whiteLabelResources } from "./resources";
import { WhiteLabelContactButton } from "./types";

const homeCountry = getHomeCountryLabel();
const currencyCode = LOCALISATION_CONFIG.currencyCode;

const contactButtons: WhiteLabelContactButton[] = [
  {
    title: "InsuranceHub bot",
    subtitle: "Message us",
  },
  {
    title: "WhatsApp",
    subtitle: "Chat with us",
    href: "https://wa.me/6005089000",
  },
];

export const whiteLabelContent = {
  app: {
    title: "Default Theme",
    faviconPath: whiteLabelResources.App_Url.public.BrandLogo,
  },
  brand: {
    logo: {
      src: whiteLabelResources.App_Url.public.BrandLogo,
      alt: "InsuranceHub logo",
      width: 300,
      height: 60,
    },
    fallbackText: "Default Theme",
  },
  company: {
    poweredBy: "Nexsys",
    legalEntity: "InsuranceHub Insurance Brokers L.L.C",
  },

  contact: {
    phoneLabel: "000 000 000",
    phoneValue: "0000000000",
    whatsappNumber: "0000000000",
    generalEmail: "",
    customerServiceEmail: "",
    chatbotAlertMessage: "Chatbot feature coming soon!",
    chatbotName: "InsuranceHub",
  },
  homepage: {
    hero: {
      eyebrow: "Getting insurance can be confusing. InsuranceHub makes it simple",
      titleLines: ["Click. Pick.", "Insure."],
      ctaLabel: "GET QUOTE",
      featuredProductPath: App_Url_New.D2CPageUrl.insurance.travel,
    },
    stats: {
      dealsTitle: "Exciting deals",
      dealsSubtitle: "Limited period only",
      policyholdersCount: "25K",
      policyholdersLabel: "Happy policyholders",
    },
    whyChooseUs: {
      heading: "Why go with InsuranceHub?",
      imageAlt: "Why InsuranceHub",
      features: [
        {
          title: "Instant Coverage",
          description: "Get covered, anytime, anywhere with our simple digital platform.",
        },
        {
          title: "Top insurers",
          description: "We help you get the right product from trusted providers.",
        },
        {
          title: "Backed by the best",
          description: "We’re backed by Nexsys, with decades of digital insurance experience.",
        },
        {
          title: "Expert assistance",
          description: "Claim the stress-free way with friendly specialists by your side.",
        },
      ],
    },
    offers: {
      heading: "Tap into limited time offers",
      items: [
        {
          title: `Save ${currencyCode} 750*`,
          description: "Get a refund on your first eligible claim.",
          terms: "* Comprehensive policies only for individuals",
        },
        {
          title: "Enjoy ZERO interest",
          description:
            "Easy Payment Plan on Nexsys Credit Card - 3, 6, 9 and 12 months tenor available.",
          terms: "Terms and conditions",
        },
        {
          title: "Free car wash",
          description:
            "And discount on car services with AG Cars offered by Sukoon Insurance Company.",
          terms: "Terms and conditions",
        },
      ],
    },
    products: {
      eyebrowPrefix: "Click. Pick.",
      eyebrowHighlight: "Insure.",
      heading: "Top protection in your hands",
      ctaLabel: "GET QUOTE",
    },
    mascot: {
      heading: "Meet P-Go",
      description: "Your go-to buddy for quick and fuss-free insurance solutions",
      imageAlt: "P-Go mascot",
      pillars: [
        {
          title: "Buy",
          description: "Digitally or guided by friendly experts.",
        },
        {
          title: "Claim",
          description: "We'll help, so you can have peace-of-mind.",
        },
        {
          title: "Keep track",
          description: "With all your policy documents, secure and in one place.",
        },
      ],
    },
    contactSection: {
      heading: "Got question?",
      buttons: contactButtons,
    },
  },
  healthInsurancePage: {
    hero: {
      eyebrowPrefix: "Click. Pick.",
      eyebrowHighlight: "Insure.",
      title: "Health Insurance",
      subtitle: "Reliable health insurance made easy",
      ctaLabel: "GET QUOTE",
      imageAlt: "Health insurance InsuranceHub mascot",
    },
    whyBuy: {
      heading: `Why buy health insurance in ${homeCountry}?`,
      reasons: [
        {
          title: "It's mandatory",
          description:
            "Employers cover employees, but you'll need separate plans for your spouse, kids, or household help.",
        },
        {
          title: "Shield yourself from costly medical bills",
          description: `Healthcare in ${homeCountry} can be expensive. Insurance helps protect you from unexpected costs.`,
        },
        {
          title: "Get access to top hospitals & specialists",
          description:
            "Enjoy coverage at leading clinics for check-ups, screenings, and emergency care.",
        },
        {
          title: "Relax knowing your loved ones are covered",
          description: "Insurance gives you peace of mind, even when you're far from home.",
        },
      ],
    },
    howToChoose: {
      heading: "How to choose the right health insurance plan",
      description: `Choosing the right health insurance plan in ${homeCountry} requires careful consideration of your needs, budget, and local requirements. Here's a step-by-step guide to help you make the best choice:`,
      items: [
        {
          id: "rules",
          title: `Understand health insurance rules in ${homeCountry}`,
          content: [
            {
              type: "paragraph",
              label: "Mandatory Coverage:",
              text: `Health insurance requirements in ${homeCountry} may vary by location, employer, and the people covered under your policy.`,
            },
            {
              type: "paragraph",
              label: "Local Compliance:",
              text: "Ensure the policy meets applicable local regulations.",
            },
          ],
        },
        {
          id: "needs",
          title: "Assess your healthcare needs",
          content: [
            {
              type: "list",
              items: [
                {
                  label: "Individual or Family:",
                  text: "If you have dependents, choose a plan that covers maternity, pediatric care, and vaccinations.",
                },
                {
                  label: "Pre-existing Conditions:",
                  text: "If you have chronic illnesses (diabetes, hypertension), ensure the policy covers pre-existing conditions.",
                },
                {
                  label: "Regular Check-ups & Preventive Care:",
                  text: "Some plans offer annual screenings, wellness programs, and vaccinations.",
                },
              ],
            },
          ],
        },
        {
          id: "coverage",
          title: "Compare coverage & benefits",
          content: [
            {
              type: "list",
              intro: "Look for a policy that includes:",
              items: [
                {
                  text: "Inpatient & Outpatient Care – Hospitalisation, doctor visits, diagnostic tests.",
                },
                {
                  text: "Maternity & Newborn Care – Coverage for pregnancy, delivery, and baby care.",
                },
                {
                  text: "Emergency & Ambulance Services – Road ambulance and emergency treatment.",
                },
                { text: "Pharmacy & Medication Cover – Ensure prescribed medicines are included." },
                {
                  text: "Specialist Consultations – Coverage for consultations with specialists and referrals.",
                },
                {
                  text: "Dental & Optical Coverage (if needed) – Some policies offer optional dental and eye care.",
                },
                {
                  text: "Alternative Medicine – Covers the cost of medication for other treatments such as Homeopathy or Ayurveda.",
                },
              ],
            },
          ],
        },
        {
          id: "network",
          title: "Check the insurance network",
          content: [
            {
              type: "paragraph",
              label: "Hospitals & Clinics:",
              text: "Ensure the policy includes hospitals and clinics near your home or workplace.",
            },
            {
              type: "paragraph",
              label: "Direct Billing vs. Reimbursement:",
              text: "Some plans offer cashless treatment, while others require pay & claim reimbursement.",
            },
          ],
        },
        {
          id: "premiums",
          title: "Compare premiums & co-payments",
          content: [
            {
              type: "paragraph",
              label: "Premium:",
              text: "The amount you pay for the policy (monthly/annually).",
            },
            {
              type: "paragraph",
              label: "Co-payment:",
              text: "A percentage (e.g., 20%) that you pay for medical services.",
            },
          ],
        },
      ],
    },
    cta: {
      prompt: "Ready to get health insurance?",
      ctaLabel: "GET QUOTE",
    },
    benefits: {
      heading: "Your health insurance benefits",
      cards: [
        {
          imageAlt: "Doctor consultation with InsuranceHub mascot",
          items: [
            "Worldwide cover available",
            "Choose your co-pay (pick the option that suits your budget)",
            "Get a second medical opinion (so you feel confident)",
            "Save 10% on a family plan (if you're covering four)",
          ],
        },
        {
          imageAlt: "Medical equipment with doctor",
          items: [
            "Maternity & newborn care (for expecting parents)",
            "Dental cover available",
            "Optical cover available",
            "Alternative therapy cover (homeopathy, Ayurveda, and more)",
          ],
        },
      ],
    },
    whyChoose: {
      heading: "Why choose InsuranceHub?",
      features: [
        {
          icon: "zap",
          title: "Exciting Propositions",
          description:
            "Enjoy special offers for everyone, get global emergency medical assistance anytime, anywhere, and access our preferred healthcare network, MedNet, NAS and Nextcare.",
        },
        {
          icon: "shield",
          title: "Backed by Nexsys",
          description:
            "You're in good hands. InsuranceHub is powered by Nexsys, with deep digital insurance experience.",
        },
        {
          icon: "headset",
          title: "Professional advisors",
          description:
            "Based on your unique needs, our experts will help you figure out the best fit!",
        },
      ],
    },
  },
  auth: {
    heroTitle: "Manage Your Insurance Business Smarter",
    heroDescription:
      "Your complete CRM solution for tracking quotes, policies, and client relationships across all insurance categories.",
    features: [
      {
        title: "Multi-Insurer Management",
        description: "RAK, GIG, Salama, Sukoon, Oriental",
        dotClassName: "bg-theme-success-strong",
      },
      {
        title: "Complete Pipeline Tracking",
        description: "From quote to policy issuance",
        dotClassName: "bg-theme-success-strong",
      },
    ],
    copyrightText: "© 2026 InsuranceHub Insurance Portal. All rights reserved.",
    brandSubtitle: "Nexsys",
    welcomeTitle: "Welcome Back",
    welcomeDescription: "Enter your credentials to access your dashboard",
    submitLabel: "Sign in",
    rememberLabel: "Remember me for 30 days",
    helpText: "Need access?",
    helpCtaLabel: "Contact your administrator",
    legalPrefix: "By signing in, you agree to our",
    legalLinks: [
      { label: "Terms of Service", href: "#" },
      { label: "Privacy Policy", href: "#" },
    ],
    otpFooterPrefix: "New to InsuranceHub?",
    otpFooterCta: "Get a quote",
    otpFooterSuffix: "to get started.",
  },
  authOc: {
    heroTitle: "Powering Your Insurer’s Retail & Broker Journeys",
    heroDescription:
      "A unified Omni Channel platform built exclusively for your insurer to connect, manage, and streamline Retail and Broker insurance journeys from quote to policy issuance.",
    features: [
      {
        title: "Retail & Broker Journeys",
        description:
          "Seamlessly manage customer and broker journeys through one connected platform.",
        dotClassName: "bg-theme-success-strong",
      },
      {
        title: "End-to-End Insurance Journey",
        description:
          "Track and manage the complete journey from quote creation to policy issuance.",
        dotClassName: "bg-theme-success-strong",
      },
      {
        title: "Insurer-Centric Platform",
        description:
          "Built exclusively for your insurer to deliver a consistent and efficient insurance experience.",
        dotClassName: "bg-theme-success-strong",
      },
    ],
  },
  journeys: {
    marketingConsentLabel:
      "I agree to receive updates from InsuranceHub about quotes, products and promotional offers through marketing channels including SMS and Email.",
    quoteMascotAlt: "Meet P-Go Cat",
    policyPending: {
      paymentPlanHeading: "EASY PAYMENT PLAN ON Nexsys",
      paymentPlanSubtext: "Credit Card (3, 6, 9 & 12 months)",
      specialistFollowUp: "One of our InsuranceHub specialists will contact you shortly.",
    },
    travel: {
      howItWorksHeading: "It’s easy to InsuranceHub",
      whyBetterHeading: "See what makes InsuranceHub better",
      paymentPlanText: "on Nexsys Credit Card",
      trustedByManyText: "Thousands insured with InsuranceHub",
    },
  },
  footer: {
    copyrightOwner: "InsuranceHub",
    copyrightLink: "#",
    regulatoryText:
      "Insurance products and services are provided subject to applicable local laws and regulations.",
  },
} as const;
